import React from 'react';

import { classNames } from '@/helpers/classNames';
import { Capacitor } from '@capacitor/core';

import styles from './RoundedButton.module.scss';

const isCapacitor = Capacitor.isNativePlatform();

/**
 * If the `color` prop is set to `"color-scheme"` then the Chip CSS would try to use
 * a `var(--color-scheme-base)` that should be defined from another CSS classes passed in via the
 * `className` prop
 */
export type RoundedButtonProps = {
	size?: 'md' | 'lg';
	disabled?: boolean;
	className?: string;
	leadingComponent?: React.ReactElement;
	trailingComponent?: React.ReactElement;
	color?: 'default' | 'white' | 'gradient' | 'blue' | 'green' | 'inactive' | 'ghost';
} & React.HTMLAttributes<HTMLDivElement>;

export const RoundedButton: React.FC<RoundedButtonProps> = ({
	children,
	size = 'md',
	color = 'default',
	disabled,
	className,
	leadingComponent,
	trailingComponent,
	onClick,
	...rest
}) => {
	const classes = classNames([
		'PUI-RoundedButton',
		styles['root'],
		styles[`size-${size}`],
		styles[`color-${color}`],
		disabled && styles['disabled'],
		!isCapacitor && styles['hover'],
		className,
	]);

	return (
		<div
			className={classes}
			onClick={(e) => {
				if (disabled || !onClick) return;
				onClick(e);
			}}
			{...rest}
		>
			{leadingComponent && (
				<div className={styles['leading-component']}>
					{leadingComponent}
				</div>
			)}
			{children}
			{trailingComponent && (
				<div className={styles['trailing-component']}>
					{trailingComponent}
				</div>
			)}
		</div>
	);
};
